// Bayer dither hover effect
// Targets .rd-card-image elements. On hover, dissolves in a color-dithered
// version of the card image. Mouse position shifts the dither pattern.

class BayerHover {
  constructor(el) {
    this.el = el;
    this.mouse = [0.5, 0.5];
    this.reveal = 0;
    this.rafId = null;
    this.ready = false;

    const img = el.querySelector('img');
    if (!img || !window.WebGL2RenderingContext) return;

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'rd-dither-canvas';
    el.appendChild(this.canvas);

    this.gl = this.canvas.getContext('webgl2');
    if (!this.gl) { this.canvas.remove(); return; }

    this.setup();
    this.loadImage(img);

    el.addEventListener('mouseenter', () => this.animateReveal(1, 350));
    el.addEventListener('mouseleave', () => this.animateReveal(0, 300));
    el.addEventListener('mousemove', e => {
      const r = el.getBoundingClientRect();
      this.mouse = [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
      this.render();
    });

    new ResizeObserver(() => this.resize()).observe(el);
  }

  loadImage(img) {
    const load = src => {
      if (!src || src.startsWith('data:')) return;
      const ci = new Image();
      ci.crossOrigin = 'anonymous';
      ci.onload = () => {
        this.resize();
        this.imgAr = ci.naturalWidth / ci.naturalHeight;
        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.tex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, ci);
        this.ready = true;
      };
      ci.onerror = () => this.canvas.remove();
      ci.src = src;
    };

    if (img.complete) {
      load(img.currentSrc || img.src);
    } else {
      img.addEventListener('load', () => load(img.currentSrc || img.src), { once: true });
    }
  }

  resize() {
    this.canvas.width = this.el.offsetWidth;
    this.canvas.height = this.el.offsetHeight;
  }

  // Animate u_reveal toward target over duration (ms)
  animateReveal(target, duration) {
    cancelAnimationFrame(this.rafId);
    const start = performance.now();
    const from = this.reveal;

    const tick = now => {
      const t = Math.min((now - start) / duration, 1.0);
      // ease out
      this.reveal = from + (target - from) * (1 - (1 - t) * (1 - t));
      this.render();
      if (t < 1.0) {
        this.rafId = requestAnimationFrame(tick);
      }
    };

    this.rafId = requestAnimationFrame(tick);
  }

  setup() {
    const gl = this.gl;

    const vert = `#version 300 es
      in vec2 a_pos;
      out vec2 v_uv;
      void main() {
        gl_Position = vec4(a_pos, 0, 1);
        v_uv = a_pos * 0.5 + 0.5;
      }`;

    // 4x4 Bayer color dither + hash-based dissolve reveal
    const frag = `#version 300 es
      precision highp float;
      uniform sampler2D u_tex;
      uniform vec2 u_mouse;
      uniform float u_reveal;
      uniform float u_img_ar;
      uniform float u_canvas_ar;
      in vec2 v_uv;
      out vec4 out_color;

      // 4x4 Bayer ordered dither matrix
      float bayer(vec2 pos) {
        int x = int(mod(pos.x, 4.0));
        int y = int(mod(pos.y, 4.0));
        int m[16] = int[]( 0, 8, 2,10, 12, 4,14, 6, 3,11, 1, 9, 15, 7,13, 5);
        return (float(m[x + y * 4]) + 0.5) / 16.0;
      }

      // Hash for dissolve pixel mask
      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
      }

      void main() {
        // Dissolve: reject pixels whose hash exceeds reveal progress
        // ~3px blocks keep the pixelated home-page aesthetic
        if (hash(floor(gl_FragCoord.xy / 3.0)) >= u_reveal) discard;

        // Replicate object-fit: cover so dither aligns with the visible image
        vec2 uv = v_uv;
        if (u_img_ar > u_canvas_ar) {
          float s = u_canvas_ar / u_img_ar;
          uv.x = uv.x * s + (1.0 - s) * 0.5;
        } else {
          float s = u_img_ar / u_canvas_ar;
          uv.y = uv.y * s + (1.0 - s) * 0.5;
        }

        vec3 rgb = texture(u_tex, vec2(uv.x, 1.0 - uv.y)).rgb;

        // Color Bayer dither — each channel thresholded independently
        // Mouse position shifts the pattern
        vec2 pos = gl_FragCoord.xy + u_mouse * 8.0;
        float t = bayer(pos);
        out_color = vec4(step(t, rgb), 1.0);
      }`;

    const vs = this.makeShader(gl.VERTEX_SHADER, vert);
    const fs = this.makeShader(gl.FRAGMENT_SHADER, frag);
    this.prog = gl.createProgram();
    gl.attachShader(this.prog, vs);
    gl.attachShader(this.prog, fs);
    gl.linkProgram(this.prog);

    this.uMouse = gl.getUniformLocation(this.prog, 'u_mouse');
    this.uReveal = gl.getUniformLocation(this.prog, 'u_reveal');
    this.uImgAr = gl.getUniformLocation(this.prog, 'u_img_ar');
    this.uCanvasAr = gl.getUniformLocation(this.prog, 'u_canvas_ar');

    // Fullscreen quad
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(this.prog, 'a_pos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    // Texture
    this.tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  }

  makeShader(type, src) {
    const gl = this.gl;
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    return s;
  }

  render() {
    if (!this.ready) return;
    const gl = this.gl;
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.prog);
    gl.uniform2fv(this.uMouse, this.mouse);
    gl.uniform1f(this.uReveal, this.reveal);
    gl.uniform1f(this.uImgAr, this.imgAr || 1.0);
    gl.uniform1f(this.uCanvasAr, this.canvas.width / this.canvas.height);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}

function init() {
  document.querySelectorAll('.rd-card-image:not([data-dither])').forEach(el => {
    el.dataset.dither = '1';
    new BayerHover(el);
  });
}

document.addEventListener('DOMContentLoaded', init);
new MutationObserver(init).observe(document.body, { childList: true, subtree: true });
